/**
 * @file i18nMixin.js
 * @description Mixin for Neptune web components to add i18n support
 * Automatically handles locale changes and re-renders components
 * Usage in a component:
 *   class MyComponent extends i18nMixin(NeptuneElement) {
 *       // Component now supports translations automatically
 *   }
 */

import { i18n } from './i18n.js';

/**
 * Mixin factory that adds i18n support to a component
 * @param {class} BaseClass - The base component class (typically NeptuneElement)
 * @returns {class} Enhanced component class with i18n support
 */
export function i18nMixin(BaseClass) {
    return class I18nComponent extends BaseClass {
        #unsubscribeLocaleChange = null;

        /**
         * @description Get a translated string
         * @param {string} key - Translation key (e.g., 'sidenav.collapse')
         * @param {Object} [params={}] - Parameters for interpolation
         * @returns {string} Translated string
         */
        translate(key, params = {}) {
            return i18n.translate(key, params);
        }

        /**
         * @description Get the current locale
         * @returns {string} Current locale code
         */
        getCurrentLocale() {
            return i18n.getLocale();
        }

        /**
         * @description Override connectedCallback to set up locale change listener
         */
        async connectedCallback() {
            await super.connectedCallback();
            
            // Subscribe to locale changes
            this.#unsubscribeLocaleChange = i18n.onLocaleChange(() => {
                if (this.isRendered) {
                    this.#reRenderWithTranslations();
                }
            });
        }

        /**
         * Override disconnectedCallback to clean up listener
         */
        async disconnectedCallback() {
            if (this.#unsubscribeLocaleChange) {
                this.#unsubscribeLocaleChange();
            }
            await super.disconnectedCallback();
        }

        /**
         * Re-render component with new translations
         * Override this method in components that need custom re-render logic
         * @private
         */
        #reRenderWithTranslations() {
            if (typeof this.onLocaleChange === 'function') {
                this.onLocaleChange();
            } else {
                this.isRendered = false;
                this.shadowRoot?.replaceChildren();
                this.connectedCallback();
            }
        }

        /**
         * Override this method in components to handle locale changes
         * Called when locale changes and component is rendered
         * @example
         * onLocaleChange() {
         *     this.shadowRoot.querySelector('.button-label').textContent = this.translate('button.label');
         * }
         */
        onLocaleChange() {
            // Stub method - override in child components
        }
    };
}

export default i18nMixin;
